import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { BlobProvider } from "@react-pdf/renderer";
import { showGirvi } from "../erpClient";
import { node_imageBaseUrl } from "../pages/const";
import DhiranSlipPDF from "./DhiranSlipPDF";

const getSlipRecordId = (record) =>
  record?.id ??
  record?.reverse_girvi_id ??
  record?.reverse_girvi_document_id ??
  record?.girvi_id ??
  record?.girvi_document_id ??
  record?.girvi_details?.id ??
  record?.reverse_girvi_details?.id ??
  record?.raw?.id ??
  null;

/** Absolute URL for @react-pdf Image (relative paths often fail). */
export const resolveGirviImageUrl = (rawPath) => {
  if (!rawPath) return null;
  const p = String(rawPath).trim();
  if (!p) return null;
  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return p;
  }
  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  if (!base) return p.startsWith("/") ? p : `/${p}`;
  const normalized = p.replace(/^\/+/, "");
  return `${base}/${normalized}`;
};

const formatSlipItem = (item) => ({
  name:
    item?.item_category_name ||
    item?.itemCategoryName ||
    item?.tag_no ||
    item?.description ||
    item?.name ||
    "",
  pcs: Number(item?.pcs ?? 0),
  gross_weight: Number(item?.gwt ?? item?.gross_weight ?? 0),
  loss_weight: Number(item?.lwt ?? item?.loss_weight ?? 0),
  net_weight: Number(item?.nwt ?? item?.net_weight ?? 0),
  fine_weight: Number(item?.fine_wt ?? item?.fine_weight ?? 0),
  purity: Number(item?.purity ?? 0),
  rate_on: item?.rate_on ?? "",
  rate: Number(item?.rate ?? 0),
  other_charges: Number(item?.other_charges ?? 0),
  value: Number(item?.totalamt ?? item?.total_amt ?? item?.value ?? 0),
  mrp: Number(item?.mrp ?? 0),
  tag_no: item?.tag_no || "",
  product_group_id: item?.product_group_id ?? null,
  girvi_item_id: item?.girvi_item_id ?? item?.id ?? null,
  image: item?.image || null,
  description:
    item?.item_category_name ||
    item?.itemCategoryName ||
    item?.tag_no ||
    item?.description ||
    item?.name ||
    "",
});

const normalizeSlipData = (response, fallbackData) => {
  const data = response?.data;
  const firstObj = Array.isArray(data) ? data[0] : data || {};
  const girviDetails =
    firstObj?.reverse_girvi_details ||
    firstObj?.girvi_details ||
    firstObj?.detail ||
    firstObj ||
    {};
  const items = Array.isArray(firstObj?.items) ? firstObj.items : [];
  const payments = Array.isArray(firstObj?.payments) ? firstObj.payments : [];

  const itemValueFromItems = items.reduce((sum, item) => {
    const amount = Number(
      item?.totalamt ?? item?.total_amt ?? item?.value ?? 0,
    );
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);

  return {
    ...(fallbackData || {}),
    ...girviDetails,
    id: girviDetails.id ?? firstObj.id ?? fallbackData?.id ?? null,
    girvi_number:
      girviDetails.reverse_girvi_number ??
      girviDetails.girvi_number ??
      firstObj.reverse_girvi_number ??
      firstObj.girvi_number ??
      fallbackData?.reverse_girvi_number ??
      fallbackData?.girvi_number ??
      "",
    reverse_girvi_number:
      girviDetails.reverse_girvi_number ??
      firstObj.reverse_girvi_number ??
      fallbackData?.reverse_girvi_number ??
      "",
    girvi_date:
      girviDetails.reverse_girvi_date ??
      girviDetails.start_date ??
      girviDetails.doc_date ??
      girviDetails.girvi_date ??
      firstObj.girvi_date ??
      fallbackData?.girvi_date ??
      "",
    due_date:
      girviDetails.due_date ??
      firstObj.due_date ??
      fallbackData?.due_date ??
      "",
    customer_name:
      girviDetails.customer_name ??
      girviDetails.supplier_name ??
      firstObj.customer_name ??
      fallbackData?.customer_name ??
      "",
    customer_mobile:
      girviDetails.customer_mobile ??
      girviDetails.mobile ??
      firstObj.customer_mobile ??
      fallbackData?.customer_mobile ??
      "",
    customer_address:
      girviDetails.address ??
      girviDetails.customer_address ??
      firstObj.customer_address ??
      fallbackData?.customer_address ??
      fallbackData?.customer_mobile ??
      "",
    adhar_card:
      girviDetails.adhar_card ??
      girviDetails.aadhar_no ??
      firstObj.adhar_card ??
      firstObj.aadhar_no ??
      fallbackData?.adhar_card ??
      fallbackData?.aadhar_no ??
      fallbackData?.aadharNo ??
      "",
    pan_card:
      girviDetails.pan_card ??
      girviDetails.pan_no ??
      firstObj.pan_card ??
      firstObj.pan_no ??
      fallbackData?.pan_card ??
      fallbackData?.pan_no ??
      fallbackData?.panNo ??
      "",
    customer_photo:
      girviDetails.customer_photo ??
      girviDetails.photo_url ??
      girviDetails.photo ??
      girviDetails.customer_image ??
      firstObj.customer_photo ??
      firstObj.photo_url ??
      firstObj.photo ??
      firstObj.customer_image ??
      fallbackData?.customer_photo ??
      fallbackData?.photo_url ??
      fallbackData?.photo ??
      fallbackData?.customer_image ??
      null,
    items_image:
      firstObj.items_image ??
      girviDetails.items_image ??
      fallbackData?.items_image ??
      items.find((it) => it.image)?.image ??
      null,
    user_proof_img:
      firstObj.user_proof_img ??
      girviDetails.user_proof_img ??
      fallbackData?.user_proof_img ??
      null,
    customer_signature:
      firstObj.customer_signature ??
      girviDetails.customer_signature ??
      fallbackData?.customer_signature ??
      null,
    note:
      girviDetails.note ??
      girviDetails.remarks ??
      firstObj.note ??
      firstObj.remarks ??
      fallbackData?.note ??
      fallbackData?.remarks ??
      "",
    principal_given:
      Number(
        girviDetails.principal_taken ??
          girviDetails.principal_amount ??
          girviDetails.principal_given ??
          firstObj.principal_taken ??
          firstObj.principal_given ??
          fallbackData?.principal_taken ??
          fallbackData?.principal_given ??
          0,
      ) || 0,
    principal_taken:
      Number(
        girviDetails.principal_taken ??
          girviDetails.principal_amount ??
          girviDetails.principal_given ??
          fallbackData?.principal_taken ??
          0,
      ) || 0,
    remaining_amount:
      Number(
        girviDetails.remaining_amount ??
          firstObj.remaining_amount ??
          fallbackData?.remaining_amount ??
          0,
      ) || 0,
    interest_percent:
      Number(
        girviDetails.interest_percent ??
          firstObj.interest_percent ??
          fallbackData?.interest_percent ??
          0,
      ) || 0,
    tenure_months:
      Number(
        girviDetails.tenure_months ??
          firstObj.tenure_months ??
          fallbackData?.tenure_months ??
          0,
      ) || 0,
    return_period:
      Number(
        girviDetails.return_period ??
          firstObj.return_period ??
          fallbackData?.return_period ??
          girviDetails.tenure_months ??
          firstObj.tenure_months ??
          0,
      ) || 0,
    status:
      girviDetails.status ?? firstObj.status ?? fallbackData?.status ?? "",
    total_items:
      Number(
        girviDetails.total_items ??
          firstObj.total_items ??
          fallbackData?.total_items ??
          items.length,
      ) || items.length,
    item_value:
      Number(
        girviDetails.item_value ??
          firstObj.item_value ??
          fallbackData?.item_value ??
          itemValueFromItems,
      ) || 0,
    place:
      girviDetails.place ??
      girviDetails.city ??
      firstObj.place ??
      firstObj.city ??
      fallbackData?.place ??
      fallbackData?.city ??
      "",
    payments,
    items:
      items.length > 0 ? items.map(formatSlipItem) : fallbackData?.items || [],
  };
};

function DhiranSlip({
  show,
  onHide,
  girviData,
  fetchDocument = showGirvi,
  title = "Dhiranslip",
  variant = "girvi",
}) {
  const [loading, setLoading] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [error, setError] = useState("");

  // Handle PDF generation
  useEffect(() => {
    let cancelled = false;

    const loadSlipData = async () => {
      if (!show) return;

      const slipId = getSlipRecordId(girviData);
      setLoading(true);
      setError("");

      try {
        if (slipId) {
          const response = await fetchDocument({ id: slipId });
          const normalizedData = normalizeSlipData(response, girviData);
          if (!cancelled) {
            setPdfData(normalizedData);
          }
        } else {
          const normalizedData = normalizeSlipData(
            { data: girviData },
            girviData,
          );
          if (!cancelled) {
            setPdfData(normalizedData);
          }
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.message || "Failed to load slip data");
          setPdfData(normalizeSlipData({ data: girviData }, girviData));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (show && girviData) {
      loadSlipData();
    } else {
      setPdfData(null);
      setLoading(false);
      setError("");
    }

    return () => {
      cancelled = true;
    };
  }, [show, girviData, fetchDocument]);

  // Handle modal close
  const handleClose = () => {
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {title}{" "}
          {(pdfData?.reverse_girvi_number || pdfData?.girvi_number) &&
            `- ${pdfData.reverse_girvi_number || pdfData.girvi_number}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "500px" }}>
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "400px" }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading PDF...</span>
            </Spinner>
          </div>
        ) : pdfData ? (
          <BlobProvider
            document={<DhiranSlipPDF girviData={pdfData} variant={variant} />}
          >
            {({ url, loading: pdfLoading, error: blobError }) => {
              if (blobError) {
                return (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "400px" }}
                  >
                    <p className="text-danger mb-0">
                      {blobError.message || "Failed to generate PDF"}
                    </p>
                  </div>
                );
              }

              if (pdfLoading || !url) {
                return (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "400px" }}
                  >
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading PDF...</span>
                    </Spinner>
                  </div>
                );
              }

              return (
                <div style={{ width: "100%" }}>
                  {error && (
                    <div className="text-danger small mb-2">{error}</div>
                  )}
                  <iframe
                    title="Dhiran Slip Preview"
                    src={url}
                    style={{ width: "100%", height: "500px", border: "none" }}
                  />
                </div>
              );
            }}
          </BlobProvider>
        ) : (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: "400px" }}
          >
            <p className="text-muted mb-0">{error || "No data available"}</p>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default DhiranSlip;
